import moment from 'moment';
import { Dictionary } from 'ramda';
import { getInitialAllocatedMinutes } from 'api/resource-management/api';
import {
  AllocationMedium,
  ListOfResourcesToBeFetched,
  ResourceManagementApiZoomType,
  ResourceManagementGroupBy,
  ResourcesToBeFetched,
  TeamLevelAggregation,
  UserLevelAggregation,
} from 'api/resource-management/types';
import { Minutes } from 'components/organisms/user-capacity/types';
import { unreachable } from 'utils/unreachable';
import { ViewPresetId } from '../scheduler-config/view-presets/constants';
import { AllocatedMinutes, DetailedAllocationData, GroupBy } from '../types';

export const minimumDate = moment().subtract(10, 'year').startOf('year');
export const maximumDate = moment().add(10, 'year').endOf('year');

export const concat = <T, U>(array1: T[], array2: U[]): (T | U)[] =>
  // hack to avoid spread operator
  array1.concat(array2 as unknown as T[]);

export const INVALID_ROLE_ID = -1;

export const getInitialResourcesToBeFetched = <
  T extends ListOfResourcesToBeFetched,
>(): ResourcesToBeFetched<T> => ({
  userIdList: [] as unknown as T,
  roleIdList: [] as unknown as T,
  projectIdList: [] as unknown as T,
});

export const getInitialResourcesToBeFetchedDictionary =
  (): ResourcesToBeFetched<Dictionary<boolean>> => ({
    userIdList: {},
    roleIdList: {},
    projectIdList: {},
  });

export const getTotalAllocatedMinutes = (
  allocatedMinutes: AllocatedMinutes,
  ignoreSoft = false,
): Minutes =>
  allocatedMinutes[AllocationMedium.Hard] +
  (ignoreSoft ? 0 : allocatedMinutes[AllocationMedium.Soft]);

export const getDetailedAllocationData = (
  allocatedMinutes: AllocatedMinutes,
  capacity = 0,
): DetailedAllocationData => {
  const totalAllocatedMinutes = getTotalAllocatedMinutes(allocatedMinutes);
  const hardAllocatedMinutes = getTotalAllocatedMinutes(allocatedMinutes, true);
  const softAllocatedMinutes = totalAllocatedMinutes - hardAllocatedMinutes;
  const availableMinutes = capacity - totalAllocatedMinutes;
  const allocatedPercentage =
    capacity === 0
      ? totalAllocatedMinutes !== 0
        ? 101
        : 100
      : Math.round((totalAllocatedMinutes / capacity) * 100);

  return {
    totalAllocatedMinutes,
    hardAllocatedMinutes,
    softAllocatedMinutes,
    allocatedPercentage,
    availableMinutes,
  };
};

export const getAllocatedMinutesFromAggregations = (
  aggregations: (UserLevelAggregation | TeamLevelAggregation)[],
): AllocatedMinutes =>
  aggregations.reduce(
    (totalAllocatedMinutes, aggregation) => ({
      [AllocationMedium.Hard]:
        totalAllocatedMinutes[AllocationMedium.Hard] +
        aggregation.allocatedMintutes[AllocationMedium.Hard],
      [AllocationMedium.Soft]:
        totalAllocatedMinutes[AllocationMedium.Soft] +
        aggregation.allocatedMintutes[AllocationMedium.Soft],
    }),
    getInitialAllocatedMinutes(),
  );

export const asApiGroupBy = (groupBy: GroupBy): ResourceManagementGroupBy => {
  switch (groupBy) {
    case GroupBy.Users:
      return ResourceManagementGroupBy.Users;
    case GroupBy.Projects:
      return ResourceManagementGroupBy.Projects;
    default:
      return unreachable(groupBy);
  }
};

export const asApiZoomType = (
  viewPreset: ViewPresetId,
): ResourceManagementApiZoomType => {
  switch (viewPreset) {
    case ViewPresetId.Month:
    case ViewPresetId.MonthZoomed:
      return ResourceManagementApiZoomType.Month;
    case ViewPresetId.Week:
    case ViewPresetId.WeekZoomed:
      return ResourceManagementApiZoomType.Week;
    case ViewPresetId.Day:
    case ViewPresetId.DayZoomed:
      return ResourceManagementApiZoomType.Day;
    default:
      return unreachable(viewPreset);
  }
};
